import {
  Checkbox,
  FormControlLabel,
  Grid,
  Typography,
} from '@mui/material';
import { useInfiniteQuery } from '@tanstack/react-query';
import NoEvents from 'assets/images/event/no_events.png';
import EventCard from 'components/Events/EventCard';
import Loader from 'components/Loading/Loader';
import Search from 'components/Search';
import { GetAllEventsForOrganizationItem } from 'lib/graphql/entities/event/types';
import { EventStatus, useGetAllEventsForOrganizationLazyQuery } from 'lib/graphql/graphql';
import { Languages } from 'lib/i18n/i18n';
import LanguageContext from 'providers/i18n/LanguageContext';
import useOrganization from 'providers/organization/useOrganization';
import {
  SyntheticEvent,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { FormattedMessage } from 'react-intl';
import { Subtitle } from 'styles/styles';

import { EventListHeaderLayout, Filters } from './styles';

function EventList() {
  const { organizationId } = useOrganization();
  const [searchValue, setSearchValue] = useState('');
  const { language } = useContext(LanguageContext);
  const [hideArchived, setHideArchived] = useState(true);

  const [getAllEventsOrganizationQuery] = useGetAllEventsForOrganizationLazyQuery();
  const {
    data,
    fetchNextPage,
    hasNextPage,
    isLoading,
    error,
  } = useInfiniteQuery({
    getNextPageParam: (lastPage) => lastPage?.lastEvaluated,
    initialPageParam: undefined,
    queryFn: async ({ pageParam }: { pageParam?: string }) => {
      const result = await getAllEventsOrganizationQuery({
        fetchPolicy: 'network-only',
        variables: {
          organizationId: organizationId || '',
          start: pageParam,
        },
      });
      return result.data?.getAllEventsForOrganization;
    },
    queryKey: ['events'],
  });

  useEffect(() => {
    if (hasNextPage) {
      fetchNextPage();
    }
  }, [fetchNextPage, hasNextPage]);

  const rows: GetAllEventsForOrganizationItem[] = useMemo(() => {
    const events = data?.pages.flatMap(
      (page) => page?.events || [],
    ) || [];
    return events.sort((
      a: GetAllEventsForOrganizationItem,
      b: GetAllEventsForOrganizationItem,
    ) => a.name[language]!.localeCompare(b.name[language]!));
  }, [data?.pages, language]);

  const handleHideArchived = useCallback((evt: SyntheticEvent) => {
    setHideArchived((evt.target as HTMLInputElement).checked);
  }, []);

  const shownEvents = useMemo<GetAllEventsForOrganizationItem[]>(() => {
    const filters = (event: GetAllEventsForOrganizationItem) => (
      (event.name[language] || event.name[event.languages[0] as Languages] || '').toLowerCase().includes(searchValue.toLowerCase())
      && (!hideArchived || (event.status !== EventStatus.Hidden))
    );
    if (rows) {
      return rows.filter(filters);
    }
    return [];
  }, [hideArchived, language, rows, searchValue]);

  if (error) return <div>TODO</div>;

  return (
    <>
      <EventListHeaderLayout>
        <Subtitle>
          <FormattedMessage id="event_my_events" />
        </Subtitle>
        <Filters>
          <FormControlLabel
            control={<Checkbox onChange={handleHideArchived} checked={hideArchived} />}
            label={<FormattedMessage id="event_hide_archived" />}
          />
          <Search searchValue={searchValue} setSearchValue={setSearchValue} />
        </Filters>
      </EventListHeaderLayout>
      <Grid container direction="column" gap={2} marginTop="1rem">
        <Loader isLoading={isLoading}>
          {shownEvents.map((event) => (
            <EventCard event={event} key={event.id} />
          ))}
          {shownEvents.length === 0 && (
            <Grid item xs={12}>
              <Grid container direction="column" textAlign="center">
                <Grid item>
                  <img src={NoEvents} alt="" />
                </Grid>
                <Grid item>
                  <Typography variant="body1" fontWeight="medium" color="#b8b8b8">
                    <FormattedMessage id="event_no_events" />
                  </Typography>
                </Grid>
              </Grid>
            </Grid>
          )}
        </Loader>
      </Grid>
    </>
  );
}

export default EventList;
